import React, { useState, useEffect, useCallback, useRef } from "react";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";
import LayersPopup from "../../../../../../../CommonComponents/LayersPopup/LayersPopup";
import { LayersIcon } from "./ThermalHeatMapSvgs";

const LAYER_ITEMS = [
    { id: "orientation_gizmo", label: "3D Orientation Gizmo", defaultChecked: true },
    { id: "nadir_axis",        label: "Nadir Axis",           defaultChecked: false },
    { id: "roll_pitch_yaw",    label: "RPY Axis",             defaultChecked: false },
    { id: "grid",              label: "Grid",                 defaultChecked: true },
    { id: "internal_faces",    label: "Internal Faces",       defaultChecked: false },
    { id: "decks",             label: "Decks",                defaultChecked: false },
    { id: "node_temps",        label: "Node Temperatures",    defaultChecked: false, dividerBefore: true },
    { id: "subsystems",        label: "Subsystems",           defaultChecked: true },
    { id: "sync_cameras",      label: "Sync Cameras",         defaultChecked: true, fullscreenOnly: true },
];

const TOGGLE_IDS = new Set(["internal_faces", "grid", "decks", "node_temps", "subsystems", "sync_cameras"]);

// Create initial layer state from LAYER_ITEMS
const INITIAL_LAYERS = LAYER_ITEMS.map((item) => ({ ...item, checked: item.defaultChecked }));

// Helper function to convert layers state to HeatmapLayerState
const layersToState = (layers: typeof INITIAL_LAYERS): HeatmapLayerState => ({
    showGizmo:         layers.find((l) => l.id === "orientation_gizmo")?.checked ?? true,
    showNadir:         layers.find((l) => l.id === "nadir_axis")?.checked ?? false,
    showRollPitchYaw:  layers.find((l) => l.id === "roll_pitch_yaw")?.checked ?? false,
    showInternalFaces: layers.find((l) => l.id === "internal_faces")?.checked ?? false,
    showGrid:          layers.find((l) => l.id === "grid")?.checked ?? false,
    showDecks:         layers.find((l) => l.id === "decks")?.checked ?? false,
    showNodeTemp:      layers.find((l) => l.id === "node_temps")?.checked ?? false,
    showSubsystems:    layers.find((l) => l.id === "subsystems")?.checked ?? false,
    syncCameras:       layers.find((l) => l.id === "sync_cameras")?.checked ?? true,
});

export interface HeatmapLayerState {
    showGizmo: boolean;
    showNadir: boolean;
    showRollPitchYaw: boolean;
    showInternalFaces: boolean;
    showGrid: boolean;
    showDecks: boolean;
    showNodeTemp: boolean;
    showSubsystems: boolean;
    syncCameras?: boolean;
}

interface ThermalHeatMap3dLayersBtnProps {
    onLayersChange: (state: HeatmapLayerState) => void;
    hideInternalFaces?: boolean;
    hideGizmo?: boolean;
    isFullscreen?: boolean;
    onOpen?: () => void;
}

const ThermalHeatMap3dLayersBtn: React.FC<ThermalHeatMap3dLayersBtnProps> = ({ onLayersChange, hideInternalFaces = false, hideGizmo = false, isFullscreen = false, onOpen }) => {
    const [layers, setLayers] = useState(() => [...INITIAL_LAYERS]);
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                const button = popupRef.current.querySelector('button');
                if (button && button.classList.contains('active')) {
                    button.click();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Notify parent of initial layer state on mount
    useEffect(() => {
        onLayersChange(layersToState(layers));
    }, [onLayersChange, layers]);

    const toggleLayer = useCallback((id: string) => {
        setLayers((prev) => {
            const updated = TOGGLE_IDS.has(id)
                ? prev.map((l) => l.id === id ? { ...l, checked: !l.checked } : l)
                : prev.map((l) => {
                    if (TOGGLE_IDS.has(l.id)) return l;
                    return { ...l, checked: l.id === id };
                });

            onLayersChange(layersToState(updated));
            return updated;
        });
    }, [onLayersChange]);

    return (
        <div ref={popupRef}>
            <LayersPopup
                items={layers
                    .filter((l) => !(hideInternalFaces && l.id === "internal_faces"))
                    .filter((l) => !(hideGizmo && l.id === "orientation_gizmo"))
                    .filter((l) => !(l.id === "sync_cameras" && !isFullscreen))
                    .map((l) => ({
                    id: l.id,
                    label: l.label,
                    checked: l.checked,
                    dividerBefore: l.id === "node_temps" || l.id === "sync_cameras",
                }))}
                onToggle={toggleLayer}
                placement="left"
                renderTrigger={({ isOpen: popupIsOpen, onToggle }) => (
                    <CustomToolTip title="Display Layers" placement="left">
                        <button
                            className={`thm-pb-btn thm-pb-layers${popupIsOpen ? " active" : ""}`}
                            onClick={() => {
                                if (!popupIsOpen) onOpen?.();
                                onToggle();
                            }}
                        >
                            <LayersIcon active={popupIsOpen} />
                        </button>
                    </CustomToolTip>
                )}
            />
        </div>
    );
};

export default React.memo(ThermalHeatMap3dLayersBtn);
